import type { DlqMessageSummary, JobLogEntry, RunbookSearchResult } from '@dev-console/types';
import { useCallback, useRef, useState } from 'react';

import { RunbookFeedback } from '@/components/runbook-feedback';
import { useRunbook } from '@/hooks/useRunbook';

interface RunbookChatProps {
  canonicalJob: string;
  dlqMessage?: DlqMessageSummary;
  logEntries?: JobLogEntry[];
  width: number;
  minWidth: number;
  maxWidth: number;
  onWidthChange: (width: number) => void;
  onClose: () => void;
}

export function RunbookChat({
  canonicalJob,
  dlqMessage,
  logEntries,
  width,
  minWidth,
  maxWidth,
  onWidthChange,
  onClose,
}: RunbookChatProps) {
  const {
    phase,
    mapping,
    searchResults,
    streamContent,
    isStreaming,
    error,
    pickResult,
    pasteUrl,
    generateTemplate,
    thumbsUp,
    thumbsDown,
    retry,
  } = useRunbook(canonicalJob, dlqMessage, logEntries);

  const isDragging = useRef(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;

      const handleMouseMove = (ev: MouseEvent) => {
        const newWidth = Math.max(minWidth, Math.min(window.innerWidth - ev.clientX, maxWidth));
        onWidthChange(newWidth);
      };

      const handleMouseUp = () => {
        isDragging.current = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [minWidth, maxWidth, onWidthChange]
  );

  return (
    <div
      className='fixed inset-y-0 right-0 z-50 flex flex-col border-l border-gray-200 bg-white shadow-xl'
      style={{ width }}
    >
      {/* Drag handle */}
      <div
        onMouseDown={handleMouseDown}
        className='absolute inset-y-0 left-0 w-1.5 cursor-col-resize hover:bg-blue-400/40 active:bg-blue-400/60'
      />
      <Header canonicalJob={canonicalJob} onClose={onClose} />
      <div className='flex-1 space-y-4 overflow-y-auto p-4'>
        {phase === 'lookup' && <LoadingState message='Looking up runbook...' />}
        {phase === 'searching' && <LoadingState message='Searching Notion...' />}
        {phase === 'pick-result' && (
          <SearchResultsPicker results={searchResults} onPick={pickResult} onPasteUrl={pasteUrl} />
        )}
        {phase === 'no-results' && (
          <NoResults canonicalJob={canonicalJob} onPasteUrl={pasteUrl} onGenerateTemplate={generateTemplate} />
        )}
        {phase === 'summarizing' && !streamContent && (
          <div className='space-y-3'>
            {mapping && (
              <a
                href={mapping.notionUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='text-xs text-blue-600 hover:underline'
              >
                Open runbook in Notion
              </a>
            )}
            <LoadingState message='Runbook found, summarizing...' />
            <LinkDifferentRunbook onPasteUrl={pasteUrl} />
          </div>
        )}
        {(phase === 'summarizing' || phase === 'feedback') && streamContent && (
          <SummaryView content={streamContent} isStreaming={isStreaming} mapping={mapping} />
        )}
        {phase === 'feedback' && (
          <>
            <RunbookFeedback onThumbsUp={thumbsUp} onThumbsDown={thumbsDown} />
            <LinkDifferentRunbook onPasteUrl={pasteUrl} />
          </>
        )}
        {phase === 'error' && <ErrorView error={error} onRetry={retry} />}
      </div>
    </div>
  );
}

function Header({ canonicalJob, onClose }: { canonicalJob: string; onClose: () => void }) {
  return (
    <div className='flex items-center justify-between border-b border-gray-200 px-4 py-3'>
      <div>
        <h2 className='text-sm font-semibold text-gray-900'>Runbook Assistant</h2>
        <p className='max-w-[350px] truncate text-xs text-gray-500'>{canonicalJob}</p>
      </div>
      <button onClick={onClose} className='text-xl leading-none text-gray-400 hover:text-gray-600' aria-label='Close'>
        &times;
      </button>
    </div>
  );
}

function LoadingState({ message }: { message: string }) {
  return (
    <div className='flex items-center gap-2 text-sm text-gray-500'>
      <div className='size-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500' />
      {message}
    </div>
  );
}

function SearchResultsPicker({
  results,
  onPick,
  onPasteUrl,
}: {
  results: RunbookSearchResult[];
  onPick: (result: RunbookSearchResult) => void;
  onPasteUrl: (url: string) => void;
}) {
  return (
    <div className='space-y-2'>
      <p className='text-sm text-gray-600'>Found possible runbooks. Select the correct one:</p>
      {results.map((result) => (
        <button
          key={result.pageId}
          onClick={() => onPick(result)}
          className='w-full rounded-lg border border-gray-200 p-3 text-left transition-colors hover:border-blue-300 hover:bg-blue-50'
        >
          <p className='truncate text-sm font-medium text-gray-900'>{result.title}</p>
          <p className='truncate text-xs text-gray-400'>{result.url}</p>
        </button>
      ))}
      <LinkDifferentRunbook onPasteUrl={onPasteUrl} />
    </div>
  );
}

function LinkDifferentRunbook({ onPasteUrl }: { onPasteUrl: (url: string) => void }) {
  const [open, setOpen] = useState(false);
  const [urlInput, setUrlInput] = useState('');

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className='text-xs text-gray-400 hover:text-gray-600 hover:underline'>
        Link a different runbook
      </button>
    );
  }

  return (
    <div className='space-y-2 border-t border-gray-200 pt-3'>
      <label className='text-xs font-medium text-gray-600'>Paste a Notion runbook URL:</label>
      <div className='flex gap-2'>
        <input
          type='url'
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          placeholder='https://www.notion.so/...'
          className='flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500'
        />
        <button
          onClick={() => {
            if (urlInput.trim()) onPasteUrl(urlInput.trim());
          }}
          disabled={!urlInput.trim()}
          className='rounded-md bg-blue-600 px-2.5 py-1.5 text-xs text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50'
        >
          Link
        </button>
      </div>
    </div>
  );
}

function NoResults({
  canonicalJob,
  onPasteUrl,
  onGenerateTemplate,
}: {
  canonicalJob: string;
  onPasteUrl: (url: string) => void;
  onGenerateTemplate: () => Promise<string>;
}) {
  const [urlInput, setUrlInput] = useState('');
  const [templateCopied, setTemplateCopied] = useState(false);

  const handlePaste = () => {
    if (urlInput.trim()) {
      onPasteUrl(urlInput.trim());
    }
  };

  const handleGenerateTemplate = async () => {
    const template = await onGenerateTemplate();
    await navigator.clipboard.writeText(template);
    setTemplateCopied(true);
  };

  return (
    <div className='space-y-4'>
      <p className='text-sm text-gray-600'>
        No runbook found for <span className='font-mono font-medium'>{canonicalJob}</span>.
      </p>

      <div className='space-y-2'>
        <label className='text-sm font-medium text-gray-700'>Paste a Notion runbook URL:</label>
        <div className='flex gap-2'>
          <input
            type='url'
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder='https://www.notion.so/...'
            className='flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
          />
          <button
            onClick={handlePaste}
            disabled={!urlInput.trim()}
            className='rounded-md bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50'
          >
            Link
          </button>
        </div>
      </div>

      <div className='border-t border-gray-200 pt-4'>
        <p className='mb-2 text-sm text-gray-500'>Or create a new runbook:</p>
        <button
          onClick={handleGenerateTemplate}
          className='rounded-md border border-gray-300 px-3 py-2 text-sm transition-colors hover:bg-gray-50'
        >
          {templateCopied ? 'Copied to clipboard!' : 'Generate template & copy to clipboard'}
        </button>
        {templateCopied && (
          <p className='mt-2 text-xs text-gray-500'>
            Create a new runbook in{' '}
            <a
              href='https://www.notion.so'
              target='_blank'
              rel='noopener noreferrer'
              className='text-blue-600 hover:underline'
            >
              Notion
            </a>{' '}
            and link it to this job.
          </p>
        )}
      </div>
    </div>
  );
}

function SummaryView({
  content,
  isStreaming,
  mapping,
}: {
  content: string;
  isStreaming: boolean;
  mapping: { notionUrl: string } | null;
}) {
  return (
    <div className='space-y-3'>
      {mapping && (
        <a
          href={mapping.notionUrl}
          target='_blank'
          rel='noopener noreferrer'
          className='text-xs text-blue-600 hover:underline'
        >
          Open runbook in Notion
        </a>
      )}
      <div className='prose prose-sm max-w-none'>
        <div className='whitespace-pre-wrap text-sm text-gray-800'>{content}</div>
        {isStreaming && <span className='ml-0.5 inline-block h-4 w-1 animate-pulse bg-blue-500' />}
      </div>
    </div>
  );
}

function ErrorView({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className='space-y-2'>
      <p className='text-sm text-red-600'>{error}</p>
      <button
        onClick={onRetry}
        className='rounded-md border border-gray-300 px-3 py-2 text-sm transition-colors hover:bg-gray-50'
      >
        Retry
      </button>
    </div>
  );
}
